import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Paper,
  Link,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  Layers as LayersIcon,
  CallSplit as CallSplitIcon,
  WarningAmber as WarningAmberIcon,
  TimerOff as TimerOffIcon,
  Inventory2 as Inventory2Icon,
  DeleteOutline as DeleteOutlineIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  AccountTree as AccountTreeIcon,
  Block as BlockIcon,
  EmojiEvents as EmojiEventsIcon,
  TrendingDown as TrendingDownIcon,
  Checklist as ChecklistIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { get, authPut } from '../../services/apiService';
import {
  getWorkItemBreakdown,
  getDefaultSprintByCurrentDate,
} from '../../services/sprintMetricsService';

// Pull `count` sprints worth of breakdown so the selected sprint is included
// even when it is not among the most recent few.
const BREAKDOWN_COUNT = 50;
const COMPLETED_STATES = ['Done', 'Closed', 'Resolved', 'Completed'];
const NON_DELIVERABLE_TYPES = ['Task', 'Test Case'];

// Chip colors for the Quarterly Delivery status column.
const QUARTERLY_STATUS_STYLE = {
  Delivered: { bg: 'rgba(76,175,80,0.12)', color: '#4caf50' },
  'In Progress': { bg: 'rgba(26,46,34,0.1)', color: '#1A2E22' },
  'At Risk': { bg: 'rgba(215,75,0,0.12)', color: '#D74B00' },
};

// Convert the rich-text editors' HTML into readable plain text for a mailto body
// (mailto bodies cannot carry HTML/formatting). Lists become "• " bullets; block
// tags and <br> become line breaks; remaining tags are stripped.
const htmlToText = (html = '') => {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html
    .replace(/<\/(p|div|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/li>/gi, '\n');
  const text = el.textContent || el.innerText || '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
};

// Generate an ADO work item URL (mirrors SprintReportDashboard).
const getAdoWorkItemUrl = (workItemId) => {
  const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
  const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
  const baseUrl = adoOrg.includes('visualstudio.com')
    ? `https://${adoOrg}`
    : `https://dev.azure.com/${adoOrg}`;
  return `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${workItemId}`;
};

// Was this item moved to the Removed state (or flagged removed) during the sprint?
// These are excluded from Scope Creep and Missed Delivery and shown in their own
// "Removed Items" section.
const isRemoved = (item) =>
  !NON_DELIVERABLE_TYPES.includes(item.workItemType)
  && item.deleted !== true
  && (item.scopeStatus === 'removed' || item.state === 'Removed');

// Is this item a scope-creep item (added after sprint start)? Excludes Tasks/Test Cases,
// and excludes removed/deleted/moved items (they're shown elsewhere, not as scope creep).
// Removed items are excluded via isRemoved so this can't drift from the Removed Items
// section (an item can be state === 'Removed' without scopeStatus === 'removed').
const isScopeCreep = (item) =>
  (item.addedAfterStart || item.scopeCreep)
  && !NON_DELIVERABLE_TYPES.includes(item.workItemType)
  && item.deleted !== true
  && !isRemoved(item)
  && item.scopeStatus !== 'moved';

// Was this item a missed delivery?
//  - Items moved out of the iteration after start count as Missed (regardless of end date).
//  - Removed (state === 'Removed' or scopeStatus 'removed') and deleted items are not "missed"
//    (removed items get their own card).
//  - Otherwise: planned/added and not completed, once the sprint has ended (kpiMissed snapshot
//    when present, else heuristic).
const isMissed = (item, sprintEnded) => {
  if (NON_DELIVERABLE_TYPES.includes(item.workItemType)) return false;
  if (item.deleted === true) return false;
  if (isRemoved(item)) return false;                 // removed → not missed (own section)
  if (item.scopeStatus === 'moved') return true;     // moved out of the sprint → missed
  if (!sprintEnded) return false; // nothing else is "missed" until the sprint end date passes
  if (item.kpiMissed != null) return !!item.kpiMissed;
  return (item.scopeStatus === 'planned' || item.scopeStatus === 'added' || item.scopeStatus == null)
    && !COMPLETED_STATES.includes(item.state);
};

const RT_COMMANDS = [
  { cmd: 'bold', Icon: FormatBoldIcon, label: 'Bold' },
  { cmd: 'italic', Icon: FormatItalicIcon, label: 'Italic' },
  { cmd: 'underline', Icon: FormatUnderlinedIcon, label: 'Underline' },
  { cmd: 'insertUnorderedList', Icon: FormatListBulletedIcon, label: 'Bullet list' },
  { cmd: 'insertOrderedList', Icon: FormatListNumberedIcon, label: 'Numbered list' },
];

/**
 * Lightweight, zero-dependency rich text editor (contentEditable + execCommand).
 * Uncontrolled: seeds its HTML once on mount and reports changes via onChange.
 * The parent reseeds it by remounting (the editable block unmounts while loading),
 * so switching sprint/team loads fresh content without fighting the caret.
 */
const RichTextEditor = ({ value, onChange, placeholder, minHeight = 160, readOnly = false }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || '';
    // Seed once on mount only — intentionally not reacting to `value` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };
  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    if (ref.current) ref.current.focus();
    emit();
  };

  return (
    <Box sx={{ border: '1px solid rgba(26,46,34,0.2)', borderRadius: 1.5, overflow: 'hidden', bgcolor: readOnly ? '#F8F9FA' : 'inherit' }}>
      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 0.25, p: 0.5, borderBottom: '1px solid rgba(26,46,34,0.12)', bgcolor: '#F8F9FA' }}>
          {RT_COMMANDS.map(({ cmd, Icon, label }) => (
            <Tooltip key={cmd} title={label}>
              {/* onMouseDown + preventDefault keeps the editor selection while clicking the toolbar */}
              <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); exec(cmd); }} sx={{ color: '#1A2E22' }}>
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      )}
      <Box
        component="div"
        ref={ref}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={readOnly ? undefined : emit}
        onBlur={readOnly ? undefined : emit}
        data-placeholder={readOnly ? '' : placeholder}
        sx={{
          minHeight,
          p: 1.5,
          fontSize: '0.9375rem',
          lineHeight: 1.6,
          outline: 'none',
          color: readOnly ? '#666666' : 'inherit',
          '& ul, & ol': { pl: 3, m: 0 },
          '&:empty:before': { content: 'attr(data-placeholder)', color: '#666666' },
        }}
      />
    </Box>
  );
};

// A titled card wrapping a rich text editor. Module-level so its identity is stable
// (an inline component would remount the editor on every keystroke and drop the caret).
const RichSection = ({ title, Icon, accent, value, onChange, placeholder, readOnly = false }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid rgba(26,46,34,0.12)' }}>
    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
      <Box sx={{ p: 0.75, borderRadius: '8px', bgcolor: `${accent}1A`, display: 'flex' }}>
        <Icon sx={{ color: accent, fontSize: '1.2rem' }} />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    </Box>
    <RichTextEditor value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly} />
  </Paper>
);

/**
 * Sprint Review tab — per team + sprint snapshot of the key scrum metrics, plus
 * Scope Creep and Missed delivery item lists. The "Refresh Data" button fetches the
 * live bugs-created-in-sprint figure from ADO. Card layout is a flex-wrap row so more
 * metric cards can be added later.
 */
const SprintReviewTab = () => {
  const [teams, setTeams] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedSprint, setSelectedSprint] = useState('');

  const [summary, setSummary] = useState(null);
  const [breakdownRow, setBreakdownRow] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [avgMetrics, setAvgMetrics] = useState(null); // averages over the team's last 6 sprints
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noData, setNoData] = useState(false);

  // Delivered Items — curated list pinned via the Sprint Report "+" action, with
  // editable summaries. Persisted per sprint+team (shared with Sprint Report).
  const [deliveredItems, setDeliveredItems] = useState([]);
  const [impediments, setImpediments] = useState('');
  const [highlight, setHighlight] = useState('');
  const [lowlight, setLowlight] = useState('');
  const [retroActions, setRetroActions] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesMessage, setNotesMessage] = useState(null);
  const [mailMessage, setMailMessage] = useState(null);
  // Snapshot of the notes payload as last loaded/saved, to detect unsaved edits
  // (gates the "Copy for Email" warning below).
  const [lastSavedNotes, setLastSavedNotes] = useState(null);
  // Most recently removed delivered item, kept around briefly so its row can be
  // restored via the undo snackbar instead of the removal being silently permanent.
  const [justRemoved, setJustRemoved] = useState(null); // { item, index }
  const undoTimerRef = useRef(null);

  // Quarterly Delivery — items added to the Weekly Report (WSR tab) for the selected team.
  const [quarterlyItems, setQuarterlyItems] = useState([]);
  const [quarterlyLoading, setQuarterlyLoading] = useState(false);

  // Load teams + sprints on mount
  useEffect(() => {
    (async () => {
      try {
        const teamsResp = await get('/api/sprint-report/teams');
        if (teamsResp.success && Array.isArray(teamsResp.data)) {
          setTeams(teamsResp.data);
          if (teamsResp.data.length > 0) setSelectedTeam(teamsResp.data[0].id);
        }
      } catch (err) {
        console.error('Error loading teams:', err);
        setError('Failed to load teams');
      }

      try {
        const sprintsResp = await get('/api/admin/sprints');
        if (sprintsResp.success && Array.isArray(sprintsResp.data)) {
          setSprints(sprintsResp.data);
          const defaultSprint = getDefaultSprintByCurrentDate(sprintsResp.data);
          if (defaultSprint) setSelectedSprint(defaultSprint);
        }
      } catch (err) {
        console.error('Error loading sprints:', err);
      }
    })();
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedTeam || !selectedSprint) return;
    setLoading(true);
    setError(null);
    setNoData(false);
    setNotesMessage(null);
    try {
      const [summaryResp, breakdown, workItemsResp, notesResp] = await Promise.all([
        get(`/api/sprint-report/summary/${selectedSprint}/${selectedTeam}`),
        getWorkItemBreakdown([selectedTeam], BREAKDOWN_COUNT).catch(() => []),
        get(`/api/sprint-report/work-items/${selectedSprint}/${selectedTeam}`).catch(() => null),
        get(`/api/sprint-report/notes/${selectedSprint}/${selectedTeam}`).catch(() => null),
      ]);

      const notesData = notesResp?.data || {};
      const loadedItems = Array.isArray(notesData.items) ? notesData.items : [];
      const loadedImpediments = notesData.impediments || '';
      const loadedHighlight = notesData.highlight || '';
      const loadedLowlight = notesData.lowlight || '';
      const loadedRetroActions = notesData.retroActions || '';
      setDeliveredItems(loadedItems);
      setImpediments(loadedImpediments);
      setHighlight(loadedHighlight);
      setLowlight(loadedLowlight);
      setRetroActions(loadedRetroActions);
      setLastSavedNotes({
        items: loadedItems,
        impediments: loadedImpediments,
        highlight: loadedHighlight,
        lowlight: loadedLowlight,
        retroActions: loadedRetroActions,
      });
      setJustRemoved(null);

      if (summaryResp.success && summaryResp.data) {
        setSummary(summaryResp.data);
      } else {
        setSummary(null);
        setNoData(true);
      }

      const row = (breakdown || []).find((r) => r.sprintName === selectedSprint) || null;
      setBreakdownRow(row);

      const wis = Array.isArray(workItemsResp?.data?.workItems) ? workItemsResp.data.workItems : [];
      setWorkItems(wis);
    } catch (err) {
      console.error('Error loading sprint review data:', err);
      const msg = (err.message || '').toLowerCase();
      if (msg.includes('404') || msg.includes('not found') || msg.includes('no sprint data')) {
        setNoData(true);
        setSummary(null);
        setBreakdownRow(null);
        setWorkItems([]);
      } else {
        setError(err.message || 'Failed to load sprint review data');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedTeam, selectedSprint]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  // Averages over the team's last 6 sprints (Velocity, Say:Do, Scope Creep). Team-level.
  // Uses the same sources as Sprint Reports. Extracted so a Refresh can re-run it too.
  const loadAvgMetrics = useCallback(async () => {
    if (!selectedTeam) { setAvgMetrics(null); return; }
    try {
      const velResp = await get(`/api/sprint-report/velocity/${selectedTeam}/6`);
      const rows = (velResp.success && Array.isArray(velResp.data)) ? velResp.data.slice(-6) : [];
      if (rows.length === 0) { setAvgMetrics(null); return; }

      const avgVelocity = Math.round(rows.reduce((s, r) => s + (r.completedPoints || 0), 0) / rows.length);
      const avgSayDo = Math.round(
        rows.reduce((s, r) => s + (r.committedPoints > 0 ? (r.completedPoints / r.committedPoints) * 100 : 0), 0) / rows.length
      );

      // Scope creep % is per-sprint on the summary endpoint — average the last 6.
      const summaries = await Promise.allSettled(
        rows.map((r) => get(`/api/sprint-report/summary/${r.sprintName}/${selectedTeam}`))
      );
      const scPcts = summaries
        .filter((x) => x.status === 'fulfilled' && x.value?.success)
        .map((x) => x.value.data?.scopeCreep?.percentage || 0);
      const avgScopeCreep = scPcts.length ? Math.round(scPcts.reduce((s, v) => s + v, 0) / scPcts.length) : null;

      setAvgMetrics({ velocity: avgVelocity, sayDo: avgSayDo, scopeCreep: avgScopeCreep });
    } catch (err) {
      console.error('Error loading average metrics:', err);
      setAvgMetrics(null);
    }
  }, [selectedTeam]);

  useEffect(() => { loadAvgMetrics(); }, [loadAvgMetrics]);

  // Quarterly Delivery — weekly-report (WSR) items for the selected team. Team-scoped.
  // Extracted so a Refresh can re-run it too.
  const loadQuarterly = useCallback(async () => {
    if (!selectedTeam) { setQuarterlyItems([]); return; }
    setQuarterlyLoading(true);
    try {
      const resp = await get(`/api/wsr/quarterly-delivery?team=${encodeURIComponent(selectedTeam)}`);
      const rows = (resp && resp.success && Array.isArray(resp.data)) ? resp.data : [];
      setQuarterlyItems(rows);
    } catch (err) {
      console.error('Error loading quarterly delivery:', err);
      setQuarterlyItems([]);
    } finally {
      setQuarterlyLoading(false);
    }
  }, [selectedTeam]);

  useEffect(() => { loadQuarterly(); }, [loadQuarterly]);

  const handleDeliveredSummaryChange = (adoId, value) => {
    setDeliveredItems((prev) => prev.map((n) => (String(n.adoId) === String(adoId) ? { ...n, summary: value } : n)));
  };

  // Removing a delivered item keeps it around briefly for an undo snackbar instead of
  // dropping it permanently on a single click (see the Priority Issues from critique).
  const handleRemoveDelivered = (adoId) => {
    setDeliveredItems((prev) => {
      const index = prev.findIndex((n) => String(n.adoId) === String(adoId));
      if (index === -1) return prev;
      const item = prev[index];
      clearTimeout(undoTimerRef.current);
      setJustRemoved({ item, index });
      undoTimerRef.current = setTimeout(() => setJustRemoved(null), 8000);
      return prev.filter((n) => String(n.adoId) !== String(adoId));
    });
  };

  const handleUndoRemoveDelivered = () => {
    if (!justRemoved) return;
    clearTimeout(undoTimerRef.current);
    setDeliveredItems((prev) => {
      const next = [...prev];
      next.splice(Math.min(justRemoved.index, next.length), 0, justRemoved.item);
      return next;
    });
    setJustRemoved(null);
  };

  const handleSaveReview = async () => {
    setSavingNotes(true);
    setNotesMessage(null);
    try {
      const payload = {
        items: deliveredItems,
        impediments,
        highlight,
        lowlight,
        retroActions,
      };
      const data = await authPut(`/api/sprint-report/notes/${selectedSprint}/${selectedTeam}`, payload);
      if (data.success) {
        const savedItems = Array.isArray(data.data?.items) ? data.data.items : deliveredItems;
        setDeliveredItems(savedItems);
        setLastSavedNotes({ items: savedItems, impediments, highlight, lowlight, retroActions });
        setNotesMessage({ type: 'success', text: 'Sprint review notes saved.' });
      } else {
        setNotesMessage({ type: 'error', text: data.message || 'Failed to save.' });
      }
    } catch (err) {
      console.error('Error saving sprint review notes:', err);
      setNotesMessage({ type: 'error', text: err.message || 'Failed to save.' });
    } finally {
      setSavingNotes(false);
    }
  };

  // Unsaved-edits flag — gates a visible warning (not just a tooltip) before "Copy for
  // Email" so a stale/unsaved review is never copied without the user noticing.
  const hasUnsavedChanges = !!lastSavedNotes && (
    impediments !== lastSavedNotes.impediments
    || highlight !== lastSavedNotes.highlight
    || lowlight !== lastSavedNotes.lowlight
    || retroActions !== lastSavedNotes.retroActions
    || JSON.stringify(deliveredItems) !== JSON.stringify(lastSavedNotes.items)
  );

  // Derived metric values
  const metrics = summary?.metrics || {};
  const velocity = metrics.velocity;
  const scopeCreep = summary?.scopeCreep || {};

  const totalSP = metrics.totalStoryPoints || 0;          // everything in the sprint (incl. scope creep)
  const completedSP = metrics.completedStoryPoints || 0;  // completed SP (incl. completed scope-creep items)
  const scopeCreepSP = scopeCreep.addedStoryPoints || 0;  // SP added after sprint start
  // "Planned" = committed at sprint start = total minus scope creep (scope creep is NOT planned).
  const plannedSP = Math.max(0, totalSP - scopeCreepSP);
  // Say : Do = completed vs planned. Can exceed 100% when scope-creep work is also delivered
  // (e.g. 10 planned + 2 creep, all done → 12/10 = 120%).
  const sayDo = plannedSP > 0 ? Math.round((completedSP / plannedSP) * 100) : 0;

  const bugsCreated = summary?.bugsCreatedInSprint || null;
  const isClosed = !!summary?.sprint?.closed; // sprint locked by an admin → read-only

  const split = breakdownRow || {};
  const splitRows = [
    { label: 'Production Bugs', shortLabel: 'Prod Bugs', count: split.productionBugCount, sp: split.productionBugSP, color: '#d32f2f' },
    { label: 'Non-Production Bugs', shortLabel: 'Non-Prod Bugs', count: split.internalBugCount, sp: split.internalBugSP, color: '#f57c00' },
    { label: 'Stories', shortLabel: 'Stories', count: split.pbiCount, sp: split.pbiSP, color: '#00897b' },
  ];

  const sprintEnded = summary?.sprint?.endDate ? Date.now() > new Date(summary.sprint.endDate).getTime() : false;
  // In-progress sprint: velocity = all SP taken into the sprint.
  // Ended sprint: completed SP.
  const thisSprintVelocity = sprintEnded ? velocity : totalSP;
  const scopeCreepItems = workItems.filter(isScopeCreep);
  const missedItems = workItems.filter((w) => isMissed(w, sprintEnded));
  const removedItems = workItems.filter(isRemoved);

  // Code activity. Prefer the persisted GitHub summary (populated by the "Refresh" button,
  // resolved from ADO GitHub links + live GitHub PR state) — this is the authoritative
  // commits + PRs-merged-to-develop figure. Fall back to per-item fields otherwise.
  const stories = workItems.filter((w) => w.workItemType === 'Product Backlog Item');
  const storiesWithDevelopPr = stories.filter((w) => w.hasDevelopPr).length;
  const githubActivity = summary?.githubActivity || null;
  const totalCommits = githubActivity && githubActivity.totalCommits != null
    ? githubActivity.totalCommits
    : workItems.reduce((sum, w) => sum + (w.commitCount || 0), 0);
  const prsMergedToDevelop = githubActivity && githubActivity.prsMergedToDevelop != null
    ? githubActivity.prsMergedToDevelop
    : workItems.filter((w) => w.hasDevelopPr).length;

  const fmt = (n) => (n == null ? '—' : n);

  const subjectLine = `Sprint Review — ${selectedTeam} — ${selectedSprint}`;
  const richSections = [
    ['Impediments / Blockers', impediments],
    ['Highlight', highlight],
    ['Lowlight', lowlight],
    ['Retro Actions', retroActions],
  ];

  // Plain-text version (clipboard fallback).
  const buildReviewText = () => {
    const L = [];
    const section = (t) => { L.push('', t, '─'.repeat(t.length)); };
    L.push(subjectLine);
    section('Metrics');
    const avg6 = (v) => (v != null ? ` (avg last 6: ${v})` : '');
    L.push(`Velocity: ${fmt(thisSprintVelocity)} SP${sprintEnded ? '' : ' (committed, in progress)'}${avg6(avgMetrics ? `${avgMetrics.velocity} SP` : null)}`);
    L.push(`Say : Do Ratio: ${sayDo}% (${completedSP}/${plannedSP} SP done/planned)${avg6(avgMetrics ? `${avgMetrics.sayDo}%` : null)}`);
    L.push(`Scope Creep: ${scopeCreep.percentage != null ? scopeCreep.percentage + '%' : '—'} (${fmt(scopeCreep.addedItems)} items, ${fmt(scopeCreep.addedStoryPoints)} SP)${avg6(avgMetrics && avgMetrics.scopeCreep != null ? `${avgMetrics.scopeCreep}%` : null)}`);
    L.push(`Work-items split: Production Bugs ${fmt(split.productionBugCount)}, Non-Production Bugs ${fmt(split.internalBugCount)}, Stories ${fmt(split.pbiCount)}`);
    if (bugsCreated) L.push(`Bugs in Sprint: ${bugsCreated.total} (Prod ${bugsCreated.productionCount}, Non-Prod ${bugsCreated.nonProductionCount})`);
    L.push(githubActivity
      ? `GitHub Activity: ${totalCommits} commits, ${prsMergedToDevelop} PR(s) merged to develop (${storiesWithDevelopPr}/${stories.length} stories with a develop PR)` +
        (githubActivity.refreshedAt ? ` — updated ${new Date(githubActivity.refreshedAt).toLocaleString()}` : '')
      : `GitHub Activity: not yet fetched — use "Refresh"`);
    L.push(`Removed Items: ${removedItems.length}`);

    section(`Quarterly Delivery (${quarterlyItems.length})`);
    quarterlyItems.length ? quarterlyItems.forEach((it) => {
      const pct = it.completionPercentage != null ? `${it.completionPercentage}%` : '—';
      const tgt = it.targetDate ? new Date(it.targetDate).toLocaleDateString() : '—';
      L.push(`• ${it.adoId} — ${it.title || ''} [${it.workItemType}] · ${it.state || '—'} · Target ${tgt} · ${pct} · ${it.deliveryStatus}`.trimEnd());
    }) : L.push('None');

    section(`Delivered in this Sprint (${deliveredItems.length})`);
    deliveredItems.length ? deliveredItems.forEach((n) => {
      L.push(`• ${n.adoId} — ${n.title || ''}`.trimEnd());
      if (n.summary) L.push(`    ${n.summary}`);
    }) : L.push('None');

    section(`Missed Items (${missedItems.length})`);
    missedItems.length ? missedItems.forEach((i) => L.push(`• ${i.id} — ${i.title}`)) : L.push('None');

    section(`Scope Creep Items (${scopeCreepItems.length})`);
    scopeCreepItems.length ? scopeCreepItems.forEach((i) => L.push(`• ${i.id} — ${i.title}`)) : L.push('None');

    section(`Removed Items (${removedItems.length})`);
    removedItems.length ? removedItems.forEach((i) => L.push(`• ${i.id} — ${i.title || ''} (${i.team || selectedTeam || '—'})`.trimEnd())) : L.push('None');

    richSections.forEach(([title, html]) => { section(title); L.push(htmlToText(html) || '—'); });
    return L.join('\n');
  };

  // Rich HTML version (pasted into the mail compose window). Inline styles only —
  // email clients strip <style> blocks.
  const buildReviewHtml = () => {
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const h = [];
    const heading = (t) => `<h3 style="margin:18px 0 6px;font-size:15px;color:#1a202c;border-bottom:1px solid #e0e0e0;padding-bottom:4px;">${esc(t)}</h3>`;
    const link = (id) => `<a href="${getAdoWorkItemUrl(id)}" style="color:#1976d2;text-decoration:none;font-weight:600;">${esc(id)}</a>`;
    const liList = (arr) => arr.length
      ? `<ul style="margin:6px 0;padding-left:20px;">${arr.map((i) => `<li style="margin:2px 0;">${link(i.id)} — ${esc(i.title)}</li>`).join('')}</ul>`
      : '<div style="color:#888;">None</div>';

    h.push('<div style="font-family:Segoe UI,Arial,sans-serif;color:#222;font-size:14px;line-height:1.5;">');
    h.push(`<h2 style="margin:0 0 2px;font-size:20px;color:#1a202c;">Sprint Review</h2>`);
    h.push(`<div style="color:#666;margin-bottom:8px;">${esc(selectedTeam)} &middot; ${esc(selectedSprint)}</div>`);

    h.push(heading('Metrics'));
    const avg6Html = (v) => (v != null ? ` <span style="color:#888;">(avg last 6: ${esc(v)})</span>` : '');
    h.push('<ul style="margin:6px 0;padding-left:20px;">');
    h.push(`<li><b>Velocity:</b> ${fmt(thisSprintVelocity)} SP${sprintEnded ? '' : ' (committed, in progress)'}${avg6Html(avgMetrics ? `${avgMetrics.velocity} SP` : null)}</li>`);
    h.push(`<li><b>Say : Do Ratio:</b> ${sayDo}% (${completedSP}/${plannedSP} SP done/planned)${avg6Html(avgMetrics ? `${avgMetrics.sayDo}%` : null)}</li>`);
    h.push(`<li><b>Scope Creep:</b> ${scopeCreep.percentage != null ? scopeCreep.percentage + '%' : '—'} (${fmt(scopeCreep.addedItems)} items, ${fmt(scopeCreep.addedStoryPoints)} SP)${avg6Html(avgMetrics && avgMetrics.scopeCreep != null ? `${avgMetrics.scopeCreep}%` : null)}</li>`);
    h.push(`<li><b>Work-items split:</b> Production Bugs ${fmt(split.productionBugCount)}, Non-Production Bugs ${fmt(split.internalBugCount)}, Stories ${fmt(split.pbiCount)}</li>`);
    if (bugsCreated) h.push(`<li><b>Bugs in Sprint:</b> ${bugsCreated.total} (Prod ${bugsCreated.productionCount}, Non-Prod ${bugsCreated.nonProductionCount})</li>`);
    h.push(githubActivity
      ? `<li><b>GitHub Activity:</b> ${totalCommits} commits, ${prsMergedToDevelop} PR(s) merged to develop <span style="color:#888;">(${storiesWithDevelopPr}/${stories.length} stories with a develop PR${githubActivity.refreshedAt ? `; updated ${esc(new Date(githubActivity.refreshedAt).toLocaleString())}` : ''})</span></li>`
      : `<li><b>GitHub Activity:</b> <span style="color:#888;">not yet fetched — use “Refresh”</span></li>`);
    h.push(`<li><b>Removed Items:</b> ${removedItems.length}</li>`);
    h.push('</ul>');

    h.push(heading(`Quarterly Delivery (${quarterlyItems.length})`));
    if (quarterlyItems.length) {
      // Fixed column widths + nowrap so short values (ID, Type, State, Target, %, Status)
      // never wrap; only the Title column wraps for long titles. No per-row borders — a
      // single header underline reads cleaner when pasted into a mail client.
      const cols = [
        { key: 'id',     label: 'ID',         width: 64,   align: 'left',   nowrap: true },
        { key: 'title',  label: 'Title',      width: null, align: 'left',   nowrap: false },
        { key: 'type',   label: 'Type',       width: 70,   align: 'left',   nowrap: true },
        { key: 'state',  label: 'State',      width: 80,   align: 'left',   nowrap: true },
        { key: 'target', label: 'Target',     width: 90,   align: 'left',   nowrap: true },
        { key: 'pct',    label: 'Completion', width: 90,   align: 'center', nowrap: true },
        { key: 'status', label: 'Status',     width: 96,   align: 'center', nowrap: true },
      ];
      const widthCss = (w) => (w ? `width:${w}px;` : '');
      const th = (c) => `<th style="text-align:${c.align};${widthCss(c.width)}padding:6px 8px;border-bottom:2px solid #e0e0e0;color:#444;font-size:13px;${c.nowrap ? 'white-space:nowrap;' : ''}">${esc(c.label)}</th>`;
      const td = (v, c) => `<td style="text-align:${c.align};padding:6px 8px;font-size:13px;vertical-align:top;${c.nowrap ? 'white-space:nowrap;' : 'word-break:break-word;'}">${v}</td>`;
      const statusChip = (s) => {
        const st = QUARTERLY_STATUS_STYLE[s] || QUARTERLY_STATUS_STYLE['In Progress'];
        return `<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:12px;font-weight:700;white-space:nowrap;background:${st.bg};color:${st.color};">${esc(s)}</span>`;
      };
      h.push('<table style="border-collapse:collapse;width:100%;table-layout:fixed;margin:6px 0;">');
      h.push(`<thead><tr>${cols.map(th).join('')}</tr></thead><tbody>`);
      quarterlyItems.forEach((it) => {
        const pct = it.completionPercentage != null ? `${it.completionPercentage}%` : '—';
        const tgt = it.targetDate ? new Date(it.targetDate).toLocaleDateString() : '—';
        const cellFor = {
          id: link(it.adoId),
          title: esc(it.title),
          type: esc(it.workItemType),
          state: esc(it.state || '—'),
          target: esc(tgt),
          pct,
          status: statusChip(it.deliveryStatus),
        };
        h.push(`<tr>${cols.map((c) => td(cellFor[c.key], c)).join('')}</tr>`);
      });
      h.push('</tbody></table>');
    } else {
      h.push('<div style="color:#888;">None</div>');
    }

    h.push(heading(`Delivered in this Sprint (${deliveredItems.length})`));
    if (deliveredItems.length) {
      h.push('<ul style="margin:6px 0;padding-left:20px;">');
      deliveredItems.forEach((n) => {
        h.push(`<li style="margin:4px 0;">${link(n.adoId)} — ${esc(n.title)}${n.summary ? `<br><span style="color:#444;">${esc(n.summary)}</span>` : ''}</li>`);
      });
      h.push('</ul>');
    } else {
      h.push('<div style="color:#888;">None</div>');
    }

    h.push(heading(`Missed Items (${missedItems.length})`));
    h.push(liList(missedItems));

    h.push(heading(`Scope Creep Items (${scopeCreepItems.length})`));
    h.push(liList(scopeCreepItems));

    h.push(heading(`Removed Items (${removedItems.length})`));
    if (removedItems.length) {
      const rth = (t) => `<th style="text-align:left;padding:6px 8px;border-bottom:2px solid #e0e0e0;color:#444;font-size:13px;white-space:nowrap;">${esc(t)}</th>`;
      const rtd = (v, nowrap) => `<td style="text-align:left;padding:6px 8px;font-size:13px;vertical-align:top;${nowrap ? 'white-space:nowrap;' : 'word-break:break-word;'}">${v}</td>`;
      h.push('<table style="border-collapse:collapse;width:100%;table-layout:fixed;margin:6px 0;">');
      h.push(`<thead><tr><th style="text-align:left;padding:6px 8px;border-bottom:2px solid #e0e0e0;color:#444;font-size:13px;width:90px;white-space:nowrap;">ID</th>${rth('Title')}<th style="text-align:left;padding:6px 8px;border-bottom:2px solid #e0e0e0;color:#444;font-size:13px;width:160px;white-space:nowrap;">Team</th></tr></thead><tbody>`);
      removedItems.forEach((it) => {
        h.push(`<tr>${rtd(link(it.id), true)}${rtd(esc(it.title), false)}${rtd(esc(it.team || selectedTeam || '—'), true)}</tr>`);
      });
      h.push('</tbody></table>');
    } else {
      h.push('<div style="color:#888;">None</div>');
    }

    richSections.forEach(([title, html]) => {
      h.push(heading(title));
      h.push(`<div>${html && html.trim() ? html : '<span style="color:#888;">—</span>'}</div>`);
    });

    h.push('</div>');
    return h.join('');
  };

  // Copy the formatted review to the clipboard (HTML + plain-text fallback) and open
  // a new compose window in the default mail app. The user pastes (Ctrl/⌘+V) and sends.
  const handleSendMail = async () => {
    const text = buildReviewText();
    const html = buildReviewHtml();
    let copied = false;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        })]);
        copied = true;
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch (err) {
      console.warn('Clipboard copy failed:', err);
    }
    setMailMessage(copied
      ? { type: 'success', text: 'Formatted review copied. Paste it into your email with Ctrl/⌘+V, then send.' }
      : { type: 'warning', text: 'Copying to the clipboard was blocked. Allow clipboard access and try again.' });
  };

  // Reusable item list for the Scope Creep / Missed sections.
  const ItemList = ({ title, icon, accent, items, emptyText }) => (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid rgba(26,46,34,0.12)' }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Box sx={{ p: 0.75, borderRadius: '8px', bgcolor: `${accent}1A`, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Chip label={items.length} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: `${accent}1A`, color: accent, fontWeight: 700 }} />
      </Box>
      <Divider sx={{ mb: 1 }} />
      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#666666', py: 1 }}>{emptyText}</Typography>
      ) : (
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {items.map((item) => (
            <Box key={item.id} display="flex" gap={1} py={0.75} sx={{ borderBottom: '1px solid rgba(26,46,34,0.08)' }}>
              <Link
                href={getAdoWorkItemUrl(item.id)}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{ fontWeight: 600, color: accent, flexShrink: 0 }}
              >
                {item.id}
              </Link>
              <Typography variant="body2" sx={{ color: '#1A1A1A', wordBreak: 'break-word' }}>
                {item.title}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );

  return (
    <Box sx={{ p: 3, backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
      {/* Controls */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Team</InputLabel>
            <Select value={selectedTeam} label="Team" onChange={(e) => setSelectedTeam(e.target.value)}>
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>{team.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Sprint</InputLabel>
            <Select value={selectedSprint} label="Sprint" onChange={(e) => setSelectedSprint(e.target.value)}>
              {sprints.map((sprint, index) => (
                <MenuItem key={sprint.sprintName} value={sprint.sprintName}>
                  {sprint.sprintName} {index === 0 ? '(Latest)' : ''}
                </MenuItem>
              ))}
              {sprints.length === 0 && <MenuItem disabled>No synced sprints</MenuItem>}
            </Select>
          </FormControl>
        </Grid>

      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {noData && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No sprint data found for <strong>{selectedSprint}</strong> /{' '}
          <strong>{selectedTeam}</strong>. Sync this team's iterations from the
          Sprint Reports tab to populate data.
        </Alert>
      )}

      {/* First-ever load has nothing to show yet, so it's the only case that blocks with a
          full-screen spinner. Switching team/sprint afterward keeps the previous view visible
          (dimmed below) instead of unmounting it — the screen never blanks mid-meeting. */}
      {loading && !summary && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress size={48} thickness={4} />
        </Box>
      )}

      {summary && (
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(248,249,250,0.6)',
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                pt: 6,
              }}
            >
              <CircularProgress size={32} thickness={4} />
            </Box>
          )}
          <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s cubic-bezier(0.4,0,0.2,1)', pointerEvents: loading ? 'none' : 'auto' }}>
          {isClosed && (
            <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }}>
              This sprint is closed (locked by an admin). The sprint review is read-only.
            </Alert>
          )}

          {/* Metric cards — TR Deep Green is the shared structural treatment for every
              card; orange is reserved for Scope Creep alone (the "at risk" metric) per
              the Orange Scarcity Rule. Bug counts live only in Work-items Split now —
              a separate "Bugs in Sprint" card was dropped as duplicate of those numbers. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            {/* Velocity */}
            <Card elevation={0} sx={{ minWidth: 0, overflow: 'hidden', borderRadius: 3, border: '1px solid rgba(26,46,34,0.15)', background: '#fff' }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="subtitle2" fontWeight="700" noWrap sx={{ fontSize: '0.8125rem' }}>Velocity</Typography>
                  <Box sx={{ p: 0.75, borderRadius: '8px', flexShrink: 0, bgcolor: 'rgba(26,46,34,0.08)' }}>
                    <TrendingUpIcon sx={{ color: '#1A2E22', fontSize: '1.2rem' }} />
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A2E22' }}>{fmt(thisSprintVelocity)}</Typography>
                <Typography variant="caption" sx={{ color: '#666666', fontWeight: 600 }}>
                  {sprintEnded ? 'Completed Story Points' : 'Committed SP (in progress)'}
                </Typography>
                <Box sx={{ borderTop: '1px solid rgba(26,46,34,0.12)', mt: 1.5, pt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 600 }}>
                    Avg last 6 sprints: {avgMetrics ? `${avgMetrics.velocity} SP` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Say : Do Ratio */}
            <Card elevation={0} sx={{ minWidth: 0, overflow: 'hidden', borderRadius: 3, border: '1px solid rgba(26,46,34,0.15)', background: '#fff' }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="subtitle2" fontWeight="700" noWrap sx={{ fontSize: '0.8125rem' }}>Say : Do Ratio</Typography>
                  <Box sx={{ p: 0.75, borderRadius: '8px', flexShrink: 0, bgcolor: 'rgba(26,46,34,0.08)' }}>
                    <AssignmentIcon sx={{ color: '#1A2E22', fontSize: '1.2rem' }} />
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A2E22' }}>{sayDo}%</Typography>
                <Typography variant="caption" sx={{ color: '#666666', fontWeight: 600 }}>
                  {completedSP} / {plannedSP} SP (Done / Planned)
                </Typography>
                <Box sx={{ borderTop: '1px solid rgba(26,46,34,0.12)', mt: 1.5, pt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 600 }}>
                    Avg last 6 sprints: {avgMetrics ? `${avgMetrics.sayDo}%` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Scope Creep — the sole card carrying TR Orange, per the Orange Scarcity Rule */}
            <Card elevation={0} sx={{ minWidth: 0, overflow: 'hidden', borderRadius: 3, border: '1px solid rgba(215,75,0,0.35)', background: '#fff' }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="subtitle2" fontWeight="700" noWrap sx={{ fontSize: '0.8125rem' }}>Scope Creep</Typography>
                  <Box sx={{ p: 0.75, borderRadius: '8px', flexShrink: 0, bgcolor: 'rgba(215,75,0,0.12)' }}>
                    <LayersIcon sx={{ color: '#D74B00', fontSize: '1.2rem' }} />
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#D74B00' }}>
                  {scopeCreep.percentage != null ? `${scopeCreep.percentage}%` : '—'}
                </Typography>
                <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                  <Chip label={`${fmt(scopeCreep.addedItems)} items`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(215,75,0,0.1)', color: '#B33F00', '& .MuiChip-label': { px: 0.75 } }} />
                  <Chip label={`${fmt(scopeCreep.addedStoryPoints)} SP`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(215,75,0,0.1)', color: '#B33F00', '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
                <Box sx={{ borderTop: '1px solid rgba(215,75,0,0.2)', mt: 1.5, pt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#B33F00', fontWeight: 600 }}>
                    Avg last 6 sprints: {avgMetrics && avgMetrics.scopeCreep != null ? `${avgMetrics.scopeCreep}%` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Work-items split */}
            <Card elevation={0} sx={{ minWidth: 0, overflow: 'hidden', borderRadius: 3, border: '1px solid rgba(26,46,34,0.15)', background: '#fff' }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="subtitle2" fontWeight="700" noWrap sx={{ fontSize: '0.8125rem' }}>Work-items Split</Typography>
                  <Box sx={{ p: 0.75, borderRadius: '8px', flexShrink: 0, bgcolor: 'rgba(26,46,34,0.08)' }}>
                    <CallSplitIcon sx={{ color: '#1A2E22', fontSize: '1.2rem' }} />
                  </Box>
                </Box>
                {splitRows.map((r) => (
                  <Box key={r.label} py={0.75}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: r.color, flexShrink: 0 }} />
                      <Tooltip title={r.label}>
                        <Typography variant="caption" noWrap sx={{ color: '#1A1A1A', lineHeight: 1.2, fontSize: '0.7rem' }}>{r.shortLabel}</Typography>
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.5} sx={{ pl: '14px' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: r.color, lineHeight: 1 }}>{fmt(r.count)}</Typography>
                      <Typography variant="caption" sx={{ color: '#666666' }}>({fmt(r.sp)} SP)</Typography>
                    </Box>
                  </Box>
                ))}
                {!breakdownRow && (
                  <Typography variant="caption" sx={{ color: '#666666' }}>No work-item breakdown for this sprint.</Typography>
                )}
              </CardContent>
            </Card>

            {/* Code Activity (persisted from ADO sync — commits + develop-branch PRs) */}
            <Card elevation={0} sx={{ minWidth: 0, overflow: 'hidden', borderRadius: 3, border: '1px solid rgba(26,46,34,0.15)', background: '#fff' }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="subtitle2" fontWeight="700" noWrap sx={{ fontSize: '0.8125rem' }}>Code Activity</Typography>
                  <Box sx={{ p: 0.75, borderRadius: '8px', flexShrink: 0, bgcolor: 'rgba(26,46,34,0.08)' }}>
                    <AccountTreeIcon sx={{ color: '#1A2E22', fontSize: '1.2rem' }} />
                  </Box>
                </Box>
                <Box display="flex" gap={1.5}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A2E22', lineHeight: 1 }}>{totalCommits}</Typography>
                    <Typography variant="caption" sx={{ color: '#666666', fontWeight: 600 }}>Commits</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A2E22', lineHeight: 1 }}>{prsMergedToDevelop}</Typography>
                    <Typography variant="caption" sx={{ color: '#666666', fontWeight: 600, display: 'block', whiteSpace: 'nowrap' }}>PRs → develop</Typography>
                  </Box>
                </Box>
                <Tooltip title={`${storiesWithDevelopPr}/${stories.length} stories with a develop PR`}>
                  <Chip
                    label={`${storiesWithDevelopPr}/${stories.length} w/ PR`}
                    size="small"
                    sx={{
                      mt: 1,
                      height: 18,
                      fontSize: '0.65rem',
                      bgcolor: 'rgba(26,46,34,0.08)',
                      color: '#1A2E22',
                      maxWidth: '100%',
                      '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                </Tooltip>
                <Typography variant="caption" sx={{ color: '#666666', display: 'block', mt: 0.5 }}>
                  {githubActivity && githubActivity.refreshedAt
                    ? `Updated ${new Date(githubActivity.refreshedAt).toLocaleDateString()}`
                    : 'Updates via Admin → Sync from ADO'}
                </Typography>
              </CardContent>
            </Card>

          </Box>

          {/* Quarterly Delivery — items added to the Weekly Report (WSR tab) for this team.
              Collapsed by default: a manager's rollup or an engineer's sprint-detail check
              rarely needs this open on first paint (progressive disclosure). */}
          <Accordion
            elevation={0}
            disableGutters
            sx={{ mt: 4, borderRadius: '12px !important', border: '1px solid rgba(26,46,34,0.12)', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#1A2E22' }} />} sx={{ px: 3, py: 1 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
                <Box sx={{ p: 0.75, borderRadius: '8px', bgcolor: 'rgba(26,46,34,0.08)', display: 'flex' }}>
                  <AccountTreeIcon sx={{ color: '#1A2E22', fontSize: '1.2rem' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Quarterly Delivery</Typography>
                  <Typography variant="caption" sx={{ color: '#666666' }}>
                    Epics / Features / PBIs added to the Weekly Report (WSR tab) for this team.
                  </Typography>
                </Box>
                {!quarterlyLoading && (
                  <Chip label={quarterlyItems.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', bgcolor: 'rgba(26,46,34,0.08)', color: '#1A2E22', fontWeight: 700 }} />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
            {quarterlyLoading ? (
              <Box sx={{ py: 2 }}><CircularProgress size={22} /></Box>
            ) : quarterlyItems.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#666666', py: 1 }}>
                No weekly-report items for this team. Add items on the WSR tab to see them here.
              </Typography>
            ) : (
              <TableContainer sx={{ border: '1px solid rgba(26,46,34,0.1)', borderRadius: '10px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 90 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 130 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 110 }}>State</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 120 }}>Target Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 100 }} align="center">Completion</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 120 }} align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quarterlyItems.map((it) => {
                      const chip = QUARTERLY_STATUS_STYLE[it.deliveryStatus] || QUARTERLY_STATUS_STYLE['In Progress'];
                      return (
                        <TableRow key={it.adoId} hover>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Link href={getAdoWorkItemUrl(it.adoId)} target="_blank" rel="noopener noreferrer" underline="none" sx={{ fontWeight: 600, color: '#1A2E22' }}>
                              {it.adoId}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ wordBreak: 'break-word' }}>{it.title}</TableCell>
                          <TableCell>{it.workItemType}</TableCell>
                          <TableCell>{it.state || '—'}</TableCell>
                          <TableCell>{it.targetDate ? new Date(it.targetDate).toLocaleDateString() : '—'}</TableCell>
                          <TableCell align="center">{it.completionPercentage != null ? `${it.completionPercentage}%` : '—'}</TableCell>
                          <TableCell align="center">
                            <Chip label={it.deliveryStatus} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: chip.bg, color: chip.color }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            </AccordionDetails>
          </Accordion>

          {/* Delivered Items — curated via the Sprint Report "+" action, editable summaries */}
          <Paper elevation={0} sx={{ p: 3, mt: 4, borderRadius: '12px', border: '1px solid rgba(26,46,34,0.12)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Box sx={{ p: 0.75, borderRadius: '8px', bgcolor: 'rgba(26,46,34,0.08)', display: 'flex' }}>
                <Inventory2Icon sx={{ color: '#1A2E22', fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Delivered Items</Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  Items pinned from Sprint Reports. Add a summary for each.
                </Typography>
              </Box>
            </Box>

            {deliveredItems.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#666666', py: 1 }}>
                No delivered items yet. Use the “+” next to a work item on the Sprint Reports tab to add one.
              </Typography>
            ) : (
              <TableContainer sx={{ border: '1px solid rgba(26,46,34,0.1)', borderRadius: '10px' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 140 }}>ADO ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22' }}>Summary</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 60 }} align="center">Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveredItems.map((note) => (
                      <TableRow key={note.adoId} hover>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Link
                            href={getAdoWorkItemUrl(note.adoId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{ fontWeight: 600, color: '#1A2E22' }}
                          >
                            {note.adoId}
                          </Link>
                          {note.title && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#666666', mt: 0.5, wordBreak: 'break-word' }}>
                              {note.title}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={note.summary}
                            onChange={(e) => handleDeliveredSummaryChange(note.adoId, e.target.value)}
                            placeholder={isClosed ? '' : 'Write a summary of this ticket…'}
                            fullWidth
                            multiline
                            minRows={1}
                            size="small"
                            InputProps={{ readOnly: isClosed }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                          {!isClosed && (
                            <Tooltip title="Remove from delivered items">
                              <IconButton size="small" onClick={() => handleRemoveDelivered(note.adoId)} sx={{ color: '#f44336' }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {justRemoved && (
              <Alert
                severity="info"
                sx={{ mt: 2 }}
                onClose={() => setJustRemoved(null)}
                action={
                  <Button color="inherit" size="small" onClick={handleUndoRemoveDelivered}>
                    Undo
                  </Button>
                }
              >
                Removed {justRemoved.item.adoId} from delivered items.
              </Alert>
            )}
          </Paper>

          {/* Removed Items — moved to Removed state during the sprint (NOT counted as missed).
              Shown as its own always-visible section below Delivered Items, rather than a
              click-to-reveal card, since removed items are review-worthy context, not a
              secondary detail. */}
          <Paper elevation={0} sx={{ p: 3, mt: 4, borderRadius: '12px', border: '1px solid rgba(26,46,34,0.12)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Box sx={{ p: 0.75, borderRadius: '8px', bgcolor: 'rgba(26,46,34,0.08)', display: 'flex' }}>
                <DeleteOutlineIcon sx={{ color: '#1A2E22', fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Removed Items</Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  Items moved to the Removed state during the sprint (not counted as missed).
                </Typography>
              </Box>
              <Chip label={removedItems.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', bgcolor: 'rgba(26,46,34,0.08)', color: '#1A2E22', fontWeight: 700 }} />
            </Box>

            {removedItems.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#666666', py: 1 }}>
                None removed this sprint.
              </Typography>
            ) : (
              <TableContainer sx={{ border: '1px solid rgba(26,46,34,0.1)', borderRadius: '10px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 100 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1A2E22', width: 180 }}>Team</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {removedItems.map((it) => (
                      <TableRow key={it.id} hover>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Link href={getAdoWorkItemUrl(it.id)} target="_blank" rel="noopener noreferrer" underline="none" sx={{ fontWeight: 600, color: '#1A2E22' }}>
                            {it.id}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ wordBreak: 'break-word' }}>{it.title}</TableCell>
                        <TableCell>{it.team || selectedTeam || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Scope Creep + Missed item lists — collapsed by default; each opens on demand
              rather than always occupying screen space (progressive disclosure). */}
          <Accordion
            elevation={0}
            disableGutters
            defaultExpanded={scopeCreepItems.length > 0 || missedItems.length > 0}
            sx={{ mt: 4, borderRadius: '12px !important', border: '1px solid rgba(26,46,34,0.12)', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#1A2E22' }} />} sx={{ px: 3, py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Scope Creep &amp; Missed Delivery
              </Typography>
              <Chip label={scopeCreepItems.length + missedItems.length} size="small" sx={{ ml: 1.5, height: 20, fontSize: '0.7rem', bgcolor: 'rgba(26,46,34,0.08)', color: '#1A2E22', fontWeight: 700 }} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <ItemList
                  title="Scope Creep Items"
                  icon={<WarningAmberIcon sx={{ color: '#D74B00', fontSize: '1.2rem' }} />}
                  accent="#D74B00"
                  items={scopeCreepItems}
                  emptyText="No scope-creep items this sprint."
                />
                <ItemList
                  title="Missed Delivery"
                  icon={<TimerOffIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />}
                  accent="#f44336"
                  items={missedItems}
                  emptyText="No missed items this sprint."
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Retrospective free-text sections — collapsed by default; expand to write or
              review notes rather than always taking up scroll space. */}
          <Accordion
            elevation={0}
            disableGutters
            defaultExpanded={!isClosed}
            sx={{ mt: 4, borderRadius: '12px !important', border: '1px solid rgba(26,46,34,0.12)', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#1A2E22' }} />} sx={{ px: 3, py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Retrospective Notes</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <RichSection
              title="Impediments / Blockers"
              Icon={BlockIcon}
              accent="#f44336"
              value={impediments}
              onChange={setImpediments}
              placeholder="Things outside the squad's control slowing them down…"
              readOnly={isClosed}
            />
            <RichSection
              title="Highlight"
              Icon={EmojiEventsIcon}
              accent="#1A2E22"
              value={highlight}
              onChange={setHighlight}
              placeholder="One thing that got better — with evidence, a number or a specific example…"
              readOnly={isClosed}
            />
            <RichSection
              title="Lowlight"
              Icon={TrendingDownIcon}
              accent="#D74B00"
              value={lowlight}
              onChange={setLowlight}
              placeholder="One thing that did not move, and why…"
              readOnly={isClosed}
            />
            <RichSection
              title="Retro Actions"
              Icon={ChecklistIcon}
              accent="#1A2E22"
              value={retroActions}
              onChange={setRetroActions}
              placeholder="Actions agreed this sprint and what changed as a result…"
              readOnly={isClosed}
            />
          </Box>
            </AccordionDetails>
          </Accordion>

          {/* Save everything (delivered summaries + retrospective sections) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            {!isClosed && (
              <Button
                variant="contained"
                startIcon={savingNotes ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveReview}
                disabled={savingNotes}
              >
                {savingNotes ? 'Saving…' : 'Save Sprint Review'}
              </Button>
            )}
            <Tooltip title="Copies the formatted review to your clipboard — paste it into your email (Ctrl/⌘+V) and send.">
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={handleSendMail}
              >
                Copy for Email
              </Button>
            </Tooltip>
            {notesMessage && (
              <Alert severity={notesMessage.type} sx={{ flex: 1, minWidth: 240, py: 0 }} onClose={() => setNotesMessage(null)}>
                {notesMessage.text}
              </Alert>
            )}
          </Box>
          {/* Visible (not tooltip-only) warning so an unsaved review is never copied
              without the user noticing — the "Copy for Email" click still works, but
              the risk is surfaced inline rather than hidden behind a hover. */}
          {hasUnsavedChanges && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              You have unsaved changes. Save the review first so "Copy for Email" includes your latest notes.
            </Alert>
          )}
          {mailMessage && (
            <Alert severity={mailMessage.type} sx={{ mt: 2 }} onClose={() => setMailMessage(null)}>
              {mailMessage.text}
            </Alert>
          )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SprintReviewTab;
